import { useMemo, useState } from "react";
import { formatFrenchPlate } from "../lib/plate-format.js";
import { useAuth } from "../hooks/useAuth.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";
import { useAbaPendente } from "../lib/open-file.js";
import { useQueryClient } from "@tanstack/react-query";

/**
 * F-chantier-agenda E2 — PORTAL DO CLIENTE (/cliente), papel `client`
 * (rank 0, sem ERP), vínculo customers.user_id. O cliente vê a agenda do
 * CHANTIER dele (ocupação por dia/horário — sem placas de terceiros) e
 * AGENDA os próprios veículos em dia/horário; cancela enquanto 'scheduled'.
 */

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function ClientPortal() {
  const t = useT();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const meQuery = trpc.chantierAgenda.clientMe.useQuery(undefined, { enabled: !!user });
  const companies = meQuery.data?.companies ?? [];
  const [companyId, setCompanyId] = useState<number | null>(null);
  const effectiveCompanyId = companyId ?? companies[0]?.companyId ?? null;
  const me = companies.find((c) => c.companyId === effectiveCompanyId) ?? null;

  const range = useMemo(() => {
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + 30);
    return { from: ymd(from), to: ymd(to) };
  }, []);

  const agendaQuery = trpc.chantierAgenda.clientAgenda.useQuery(
    { companyId: effectiveCompanyId ?? 0, from: range.from, to: range.to },
    { enabled: effectiveCompanyId != null },
  );
  const utils = trpc.useUtils();
  const [feedback, setFeedback] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const createMutation = trpc.chantierAgenda.clientCreate.useMutation({
    onSuccess: () => {
      setFeedback({ tone: "ok", text: t("clientPortal.created") });
      setPlate("");
      setVehicleLabel("");
      void utils.chantierAgenda.clientAgenda.invalidate();
    },
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  const cancelMutation = trpc.chantierAgenda.clientCancel.useMutation({
    onSuccess: () => void utils.chantierAgenda.clientAgenda.invalidate(),
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  const logoutMutation = trpc.auth.logout.useMutation({
    onSettled: () => {
      queryClient.clear();
      window.location.assign("/login");
    },
  });

  const [date, setDate] = useState(range.from);
  const [time, setTime] = useState("09:00");
  const [plate, setPlate] = useState("");
  const [vehicleLabel, setVehicleLabel] = useState("");

  const data = agendaQuery.data;
  const occupancyByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of data?.occupancy ?? []) {
      m.set(o.date, (m.get(o.date) ?? 0) + Number(o.total));
    }
    return m;
  }, [data?.occupancy]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <header className="sticky top-0 z-10 border-b border-neutral-800 bg-neutral-900/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold tracking-wide">
            <span className="text-lime">STORM</span> · {t("clientPortal.title")}
          </h1>
          <p className="text-[11px] text-neutral-500">
            {data && "customerName" in data && data.customerName ? data.customerName : user.name}
            {data?.chantier?.name && ` · 🏗 ${data.chantier.name}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {companies.length > 1 && (
            <select
              value={effectiveCompanyId ?? ""}
              onChange={(e) => setCompanyId(Number(e.target.value))}
              className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs"
            >
              {companies.map((c) => (
                <option key={c.companyId} value={c.companyId}>
                  {c.companyName}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={() => logoutMutation.mutate()}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs hover:border-red-400 hover:text-red-300"
          >
            {t("clientPortal.logout")}
          </button>
        </div>
      </header>

      <main className="max-w-xl mx-auto p-4 space-y-5">
        {meQuery.data && !meQuery.data.isClient && (
          <p className="rounded-md bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-200">
            {t("clientPortal.notClientNote")}
          </p>
        )}
        {data && (!data.linked || data.chantier == null) && (
          <p className="rounded-md bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-200">
            {t("clientPortal.notLinkedNote")}
          </p>
        )}

        {feedback && (
          <p
            className={`rounded-md px-4 py-3 text-sm border ${
              feedback.tone === "ok"
                ? "bg-lime/10 border-lime/30 text-lime"
                : "bg-red-500/10 border-red-500/30 text-red-300"
            }`}
          >
            {feedback.text}
          </p>
        )}

        {data?.chantier && (
          <>
            {/* Agendar veículo */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setFeedback(null);
                createMutation.mutate({
                  companyId: effectiveCompanyId!,
                  date,
                  time,
                  plate: plate.trim(),
                  vehicleLabel: vehicleLabel.trim() || undefined,
                });
              }}
              className="rounded-xl border border-neutral-800 bg-neutral-900 p-4 space-y-3"
            >
              <h2 className="text-xs uppercase tracking-wider text-neutral-500">
                {t("clientPortal.bookTitle")}
              </h2>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
                  {t("clientPortal.dateLabel")}
                  <input
                    type="date"
                    value={date}
                    min={range.from}
                    max={range.to}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
                  />
                </label>
                <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
                  {t("clientPortal.timeLabel")}
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    required
                    className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
                  />
                </label>
              </div>
              {date && occupancyByDate.get(date) != null && (
                <p className="text-xs text-neutral-400">
                  {occupancyByDate.get(date)} {t("clientPortal.occupancySuffix")}
                </p>
              )}
              <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
                {t("clientPortal.plateLabel")}
                <input
                  value={plate}
                  onChange={(e) => setPlate(formatFrenchPlate(e.target.value))}
                  placeholder="AA-123-CD"
                  maxLength={12}
                  required
                  className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-center font-mono text-lg tracking-widest outline-none focus:border-lime"
                />
              </label>
              <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
                {t("clientPortal.vehicleLabel")}
                <input
                  value={vehicleLabel}
                  onChange={(e) => setVehicleLabel(e.target.value)}
                  placeholder={t("clientPortal.vehiclePh")}
                  maxLength={120}
                  className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
                />
              </label>
              <button
                type="submit"
                disabled={createMutation.isPending || plate.trim().length < 4}
                className="w-full rounded-lg bg-lime px-4 py-3 text-sm font-semibold text-on-lime disabled:opacity-40"
              >
                📅 {t("clientPortal.bookBtn")}
              </button>
            </form>

            {/* Meus agendamentos */}
            <section>
              <h2 className="mb-2 text-xs uppercase tracking-wider text-neutral-500">
                {t("clientPortal.myBookingsTitle")}
              </h2>
              {data.mine.length === 0 && (
                <p className="text-sm text-neutral-600">{t("clientPortal.myBookingsEmpty")}</p>
              )}
              <div className="space-y-2">
                {data.mine.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-neutral-800 bg-neutral-900/60 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="font-mono">{a.date}</span>
                      <span className="text-lime font-semibold"> {a.time}</span>
                      <span className="font-mono"> · {a.plate ?? "—"}</span>
                      {a.vehicleLabel && (
                        <span className="text-neutral-500"> · {a.vehicleLabel}</span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] ${
                          a.status === "scheduled"
                            ? "bg-sky-500/15 text-sky-300"
                            : a.status === "arrived"
                              ? "bg-amber-500/15 text-amber-300"
                              : "bg-lime/15 text-lime"
                        }`}
                      >
                        {a.status === "scheduled"
                          ? t("clientPortal.statusScheduled")
                          : a.status === "arrived"
                            ? t("clientPortal.statusArrived")
                            : t("clientPortal.statusDone")}
                      </span>
                      {a.status === "scheduled" && (
                        <button
                          onClick={() =>
                            cancelMutation.mutate({
                              companyId: effectiveCompanyId!,
                              appointmentId: a.id,
                            })
                          }
                          disabled={cancelMutation.isPending}
                          className="rounded-md border border-neutral-700 px-2 py-0.5 text-[11px] hover:border-red-400 hover:text-red-300"
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Ocupação do chantier */}
            <section>
              <h2 className="mb-2 text-xs uppercase tracking-wider text-neutral-500">
                {t("clientPortal.occupancyTitle")}
              </h2>
              {(data.occupancy ?? []).length === 0 && (
                <p className="text-sm text-neutral-600">{t("clientPortal.occupancyEmpty")}</p>
              )}
              <div className="space-y-1">
                {[...occupancyByDate.entries()].map(([d, n]) => (
                  <div
                    key={d}
                    className="flex items-center justify-between rounded border border-neutral-800 bg-neutral-900/40 px-3 py-1.5 text-sm"
                  >
                    <span className="font-mono">{d}</span>
                    <span className="text-neutral-400">
                      {n} {t("clientPortal.occupancySuffix")}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* F-worklog — worklogs dos técnicos que trabalharam para ESTE
            cliente na semana (com ateliê/local), com download do PDF. */}
        {effectiveCompanyId != null && <ClientWorklogs companyId={effectiveCompanyId} />}
      </main>
    </div>
  );
}

function worklogMonday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function worklogShiftWeek(weekStart: string, dir: -1 | 1): string {
  const d = new Date(weekStart + "T00:00:00");
  d.setDate(d.getDate() + dir * 7);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function worklogWeekNum(weekStart: string): number {
  const d = new Date(weekStart + "T00:00:00Z");
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}
const WORKLOG_SERVICE_LABEL: Record<string, string> = {
  dsp: "PDR",
  montage_demontage: "Montage",
  peinture: "Peinture",
  pieces: "Pièces",
};

/** F-worklog — visão do CLIENTE: técnicos da semana + PDF. */
function ClientWorklogs({ companyId }: { companyId: number }) {
  const t = useT();
  const [weekStart, setWeekStart] = useState(worklogMonday());
  const weekQ = trpc.worklogs.clientWeek.useQuery({ companyId, weekStart });
  const abaPdf = useAbaPendente();
  const pdfMut = trpc.worklogs.clientPdf.useMutation({
    onSuccess: (r) => abaPdf.ir(r.url),
    onError: () => abaPdf.fechar(),
  });
  const techs = weekQ.data?.technicians ?? [];
  return (
    <section className="pt-2">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-xs uppercase tracking-wider text-neutral-500">
          🗒 {t("worklog.clientSectionTitle")}
        </h2>
        <div className="flex items-center overflow-hidden rounded-md border border-neutral-700 text-xs">
          <button
            type="button"
            className="px-2 py-1 hover:bg-neutral-800"
            onClick={() => setWeekStart((w) => worklogShiftWeek(w, -1))}
          >
            −
          </button>
          <span className="border-x border-neutral-700 px-2.5 py-1 font-semibold">
            {t("worklog.weekPre")} {worklogWeekNum(weekStart)}
          </span>
          <button
            type="button"
            className="px-2 py-1 hover:bg-neutral-800"
            onClick={() => setWeekStart((w) => worklogShiftWeek(w, 1))}
          >
            +
          </button>
        </div>
      </div>
      {techs.length === 0 && <p className="text-sm text-neutral-600">{t("worklog.emptyWeek")}</p>}
      <div className="space-y-2">
        {techs.map((tech) => (
          <div
            key={tech.technicianId}
            className="rounded-lg border border-neutral-800 bg-neutral-900/60 p-3 space-y-1.5"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">🔧 {tech.technicianName}</p>
              <button
                type="button"
                disabled={pdfMut.isPending}
                onClick={() => {
                  abaPdf.abrir();
                  pdfMut.mutate({ companyId, technicianId: tech.technicianId, weekStart });
                }}
                className="rounded-md border border-lime/50 px-2.5 py-1 text-xs text-lime hover:bg-lime/10 disabled:opacity-40"
              >
                📄 {pdfMut.isPending ? t("worklog.generatingBtn") : t("worklog.pdfBtn")}
              </button>
            </div>
            {tech.rows.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-x-2 text-xs text-neutral-300">
                <span className="font-mono text-neutral-500">
                  {r.date.slice(8, 10)}/{r.date.slice(5, 7)}
                </span>
                <span className="font-mono font-semibold">{r.plate ?? "—"}</span>
                <span className="text-neutral-400">
                  {[r.make, r.model].filter(Boolean).join(" ")}
                </span>
                <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px]">
                  {WORKLOG_SERVICE_LABEL[r.serviceType] ?? r.serviceType}
                </span>
                {r.atelierName && (
                  <span className="text-neutral-500">
                    📍 {r.atelierName}
                    {r.atelierCity ? ` — ${r.atelierCity}` : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
